import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const EditNoteIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const SignIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
  </svg>
);

const PhotoCameraIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 15.2A3.2 3.2 0 0 1 8.8 12 3.2 3.2 0 0 1 12 8.8a3.2 3.2 0 0 1 3.2 3.2 3.2 3.2 0 0 1-3.2 3.2zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z" />
  </svg>
);

const TaskAltIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

const steps = [
  { label: 'Détails',  icon: EditNoteIcon,    path: 'details'      },
  { label: 'Contrat',  icon: SignIcon,         path: 'contract'     },
  { label: 'Photos',   icon: PhotoCameraIcon,  path: 'capture'      },
  { label: 'Caisse',   icon: TaskAltIcon,      path: 'finalization' },
];

interface Props {
  currentStep: number;
}

const PiercingWorkflowStepper: React.FC<Props> = ({ currentStep }) => {
  const navigate = useNavigate();
  const { appointmentId } = useParams<{ appointmentId: string }>();

  // Progress line fills to the mid-point of the active step, not past it
  const progressPct = steps.length > 1 ? (currentStep / (steps.length - 1)) * 100 : 0;

  return (
    <nav aria-label="Étapes du parcours piercing" className="relative mb-10 px-2">
      <ol className="relative flex items-start justify-between">
        {/* Track + progress sit exactly at icon-center (24 px = half of w-12 h-12) */}
        <div className="absolute left-0 right-0 h-[3px] rounded-full bg-gray-200 pointer-events-none" style={{ top: '24px' }} />
        <div
          className="absolute left-0 h-[3px] rounded-full bg-primary-500 pointer-events-none transition-[width] duration-300"
          style={{ top: '24px', width: `${progressPct}%` }}
        />

        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isDone = index < currentStep;
          // Only reachable steps (already completed, or the current one) can be navigated to directly
          const isReachable = index <= currentStep;
          const href = `/practitioner/piercing/${appointmentId}/${step.path}`;

          return (
            <li key={step.label} className="relative z-10 flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => isReachable && navigate(href)}
                disabled={!isReachable}
                aria-current={isActive ? 'step' : undefined}
                title={isReachable ? step.label : `${step.label} (à venir)`}
                className={`flex h-12 w-12 items-center justify-center rounded-full ring-4 ring-white shadow-sm
                  transition-all duration-200
                  ${isActive
                    ? 'bg-primary-500 text-white shadow-md scale-110'
                    : isDone
                      ? 'bg-primary-200 text-primary-600 hover:bg-primary-300 cursor-pointer'
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'}`}
              >
                <Icon className="h-6 w-6" />
              </button>
              <span
                className={`text-xs font-semibold whitespace-nowrap
                  ${isActive ? 'text-primary-600' : isDone ? 'text-gray-500' : 'text-gray-300'}`}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default PiercingWorkflowStepper;
